import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../context/AppContext";
import "bootstrap/dist/css/bootstrap.min.css";
import Loader from "./Loader";
import {
  getWithAuthCallWithErrorResponse,
  putWithAuthCall,
} from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";
import { ToastMsg } from "./ToastMsg";

// Which countries NEW members can sign up from, by their phone number's
// country. Existing members can always log in. The admin selects rows (or uses
// the per-row switch), stages availability changes, and saves them in one go;
// nothing reaches the server until "Save changes".
const GRADIENT = "linear-gradient(90deg, #055D9D 0%, #7A2E8E 55%, #E40288 100%)";

// "LK" -> 🇱🇰 (regional indicator symbols).
const flagEmoji = (code) =>
  String.fromCodePoint(...code.split("").map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

const SignupCountries = () => {
  const { sidebar } = useContext(AppContext);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Staged edits: code -> new isAvailable. Only real differences are kept.
  const [pending, setPending] = useState(() => new Map());
  const [selected, setSelected] = useState(() => new Set());

  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    load();
  }, []);

  // Warn before closing/reloading the tab with unsaved changes.
  useEffect(() => {
    if (pending.size === 0) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [pending]);

  const load = () => {
    setLoading(true);
    getWithAuthCallWithErrorResponse(ApiConfig.SIGNUP_COUNTRIES)
      .then((res) => {
        setLoading(false);
        if (res.error) {
          ToastMsg("error", res.json?.message || "Could not load countries");
          return;
        }
        setCountries(res.json?.data?.countries || []);
        setPending(new Map());
        setSelected(new Set());
      })
      .catch(() => {
        setLoading(false);
        ToastMsg("error", "Could not load countries");
      });
  };

  const effective = (c) => (pending.has(c.code) ? pending.get(c.code) : c.isAvailable);

  const regions = useMemo(
    () => [...new Set(countries.map((c) => c.region).filter(Boolean))].sort(),
    [countries]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const digits = q.replace(/^\+/, "");
    return countries.filter((c) => {
      if (region && c.region !== region) return false;
      if (status) {
        const avail = pending.has(c.code) ? pending.get(c.code) : c.isAvailable;
        if (status === "available" && !avail) return false;
        if (status === "unavailable" && avail) return false;
      }
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase() === q ||
        (/^\d+$/.test(digits) && c.dialCode.startsWith(digits))
      );
    });
  }, [countries, pending, search, region, status]);

  const counts = useMemo(() => {
    const available = countries.filter((c) =>
      pending.has(c.code) ? pending.get(c.code) : c.isAvailable
    ).length;
    return { available, unavailable: countries.length - available };
  }, [countries, pending]);

  // ---- staging ----
  const stage = (codes, value) =>
    setPending((prev) => {
      const next = new Map(prev);
      const original = new Map(countries.map((c) => [c.code, c.isAvailable]));
      codes.forEach((code) => {
        if (original.get(code) === value) next.delete(code);
        else next.set(code, value);
      });
      return next;
    });

  const stageSelected = (value) => {
    stage([...selected], value);
    setSelected(new Set());
  };

  const discard = () => {
    setPending(new Map());
    setSelected(new Set());
  };

  // ---- selection ----
  const filteredCodes = filtered.map((c) => c.code);
  const selectedInView = filteredCodes.filter((code) => selected.has(code)).length;
  const allInViewSelected = filteredCodes.length > 0 && selectedInView === filteredCodes.length;

  const headerCheckbox = useRef(null);
  useEffect(() => {
    if (headerCheckbox.current) {
      headerCheckbox.current.indeterminate = selectedInView > 0 && !allInViewSelected;
    }
  }, [selectedInView, allInViewSelected]);

  const toggleAllInView = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allInViewSelected) filteredCodes.forEach((code) => next.delete(code));
      else filteredCodes.forEach((code) => next.add(code));
      return next;
    });

  const toggleRow = (code) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  // ---- save ----
  const save = () => {
    const changes = [...pending].map(([code, isAvailable]) => ({ code, isAvailable }));
    if (changes.length === 0) return;

    const turningOff = changes.filter((c) => !c.isAvailable).length;
    if (
      turningOff > 0 &&
      !window.confirm(
        `${turningOff} ${turningOff === 1 ? "country" : "countries"} will stop accepting new sign-ups. ` +
          "Existing members there can still log in. Continue?"
      )
    ) {
      return;
    }

    setSaving(true);
    putWithAuthCall(ApiConfig.SIGNUP_COUNTRIES, JSON.stringify({ changes }))
      .then((res) => {
        setSaving(false);
        if (!res || res.error) {
          ToastMsg("error", (res && res.message) || "Save failed");
          return;
        }
        setCountries(res.data?.countries || []);
        setPending(new Map());
        setSelected(new Set());
        ToastMsg("success", "Sign-up countries saved");
      })
      .catch(() => {
        setSaving(false);
        ToastMsg("error", "Save failed");
      });
  };

  if (loading) {
    return (
      <main id="cx-main" className={sidebar ? "admin-main" : "cx-active admin-main"}>
        <Loader />
      </main>
    );
  }

  return (
    <main id="cx-main" className={sidebar ? "admin-main" : "cx-active admin-main"}>
      <div className="heading-btn-main">
        <div className="heading-common">
          <label htmlFor="">Sign-up Countries</label>
        </div>
        <div className="add-btn d-flex">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={discard}
            disabled={saving || pending.size === 0}
          >
            Discard
          </button>
          <button
            className="btn text-white"
            style={{ background: GRADIENT, border: "none" }}
            onClick={save}
            disabled={saving || pending.size === 0}
          >
            {saving ? "Saving…" : `Save changes${pending.size ? ` (${pending.size})` : ""}`}
          </button>
        </div>
      </div>

      <p className="text-muted small mb-3">
        New members can only sign up with a phone number from an available country.
        Existing members can always log in. Select countries, change their
        availability, then save.
      </p>

      {/* ---- filters ---- */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap align-items-end gap-3">
          <div style={{ flex: "2 1 240px" }}>
            <label className="text-muted mb-1 d-block" style={{ fontSize: 11 }}>
              Search
            </label>
            <input
              className="form-control"
              placeholder="Country name, code (LK) or dial code (+94)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label className="text-muted mb-1 d-block" style={{ fontSize: 11 }}>
              Region
            </label>
            <select
              className="form-select"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              <option value="">All regions</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label className="text-muted mb-1 d-block" style={{ fontSize: 11 }}>
              Status
            </label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
          <div className="small text-muted ms-auto">
            <span className="text-success fw-semibold">{counts.available} available</span>
            {" · "}
            <span className="text-danger fw-semibold">{counts.unavailable} unavailable</span>
            {pending.size > 0 && (
              <span className="text-warning fw-semibold"> · {pending.size} unsaved</span>
            )}
          </div>
        </div>
      </div>

      {/* ---- bulk actions ---- */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span className="small text-muted me-2">
          {selected.size > 0 ? `${selected.size} selected` : "Select countries to change them together"}
        </span>
        <button
          className="btn btn-sm btn-outline-success"
          disabled={selected.size === 0}
          onClick={() => stageSelected(true)}
        >
          Make available
        </button>
        <button
          className="btn btn-sm btn-outline-danger"
          disabled={selected.size === 0}
          onClick={() => stageSelected(false)}
        >
          Make unavailable
        </button>
        {selected.size > 0 && (
          <button className="btn btn-sm btn-link" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        )}
        <span className="small text-muted ms-auto">
          Showing {filtered.length} of {countries.length}
        </span>
      </div>

      <div className="card">
        <div className="table-responsive" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    ref={headerCheckbox}
                    type="checkbox"
                    className="form-check-input"
                    checked={allInViewSelected}
                    onChange={toggleAllInView}
                    title="Select all shown"
                  />
                </th>
                <th>Country</th>
                <th>Code</th>
                <th>Dial code</th>
                <th>Region</th>
                <th style={{ width: 200 }}>Availability</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const avail = effective(c);
                const changed = pending.has(c.code);
                return (
                  <tr
                    key={c.code}
                    style={changed ? { background: "rgba(255, 193, 7, 0.12)" } : undefined}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected.has(c.code)}
                        onChange={() => toggleRow(c.code)}
                      />
                    </td>
                    <td>
                      <span className="me-2" style={{ fontSize: 18 }}>
                        {flagEmoji(c.code)}
                      </span>
                      {c.name}
                    </td>
                    <td className="text-muted">{c.code}</td>
                    <td>{c.dialCode ? `+${c.dialCode}` : "—"}</td>
                    <td>
                      {c.region}
                      {c.subregion && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {c.subregion}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="form-check form-switch d-flex align-items-center gap-2 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={avail}
                          onChange={() => stage([c.code], !avail)}
                        />
                        <span
                          className={`badge ${avail ? "bg-success" : "bg-secondary"}`}
                        >
                          {avail ? "Available" : "Unavailable"}
                        </span>
                        {changed && (
                          <span className="badge bg-warning text-dark">Unsaved</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No countries match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};

export default SignupCountries;
